import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  BudgetGrid,
  BudgetImportResult,
  BudgetVsActualResponse,
  SetBudgetsInput,
} from '@accounting/shared';
import { api } from '../lib/api';

export function useBudgetGrid(companyId: string | null, year: number) {
  return useQuery({
    queryKey: ['budgets', companyId ?? 'none', year],
    enabled: companyId !== null,
    queryFn: () =>
      api.get<{ grid: BudgetGrid }>(`/companies/${companyId}/budgets`, { year: String(year) }).then((r) => r.grid),
  });
}

export function useBudgetVsActual(
  companyId: string | null,
  year: number,
  fromMonth: number,
  toMonth: number,
) {
  return useQuery({
    queryKey: ['budget-vs-actual', companyId ?? 'none', year, fromMonth, toMonth],
    enabled: companyId !== null,
    queryFn: () =>
      api.get<BudgetVsActualResponse>(`/companies/${companyId}/reports/budget-vs-actual`, {
        year: String(year),
        fromMonth: String(fromMonth),
        toMonth: String(toMonth),
      }),
  });
}

function invalidate(qc: ReturnType<typeof useQueryClient>, companyId: string | null) {
  const c = companyId ?? 'none';
  qc.invalidateQueries({ queryKey: ['budgets', c] });
  qc.invalidateQueries({ queryKey: ['budget-vs-actual', c] });
}

export function useSetBudgets(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SetBudgetsInput) => {
      if (!companyId) throw new Error('No active company');
      return api.put<{ grid: BudgetGrid }>(`/companies/${companyId}/budgets`, input).then((r) => r.grid);
    },
    onSuccess: () => invalidate(qc, companyId),
  });
}

export function useImportBudget(companyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ year, fileName, content }: { year: number; fileName: string; content: string }) => {
      if (!companyId) throw new Error('No active company');
      return api
        .post<{ result: BudgetImportResult }>(`/companies/${companyId}/budgets/import`, { year, fileName, content })
        .then((r) => r.result);
    },
    onSuccess: () => invalidate(qc, companyId),
  });
}
